import { useState, useRef, useEffect } from 'react';
import { FaTimes, FaPaperPlane } from "react-icons/fa";
import axios from 'axios';
import { BOT_RESPONSES } from '../constants/PortfolioInsight';

const FloatingSidebar = ({ isVisible, onClose }) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const STORAGE_KEY = 'chat_messages';
  const STORAGE_LOADING_KEY = 'chat_isLoading';

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);

      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            setMessages(parsed);
        }
      }

      const rawLoading = sessionStorage.getItem(STORAGE_LOADING_KEY);

      if (rawLoading === 'true') {
        setIsLoading(true);
      }
    } catch (e) {
      console.warn('Could not read chats from sessionStorage', e);
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (message.trim()) {
      const newMessage = message.trim();
      setMessage('');
      
      setMessages(prev => {
        const next = [...prev, { text: newMessage, type: 'user-message' }];
        try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
        return next;
      });

      setIsLoading(true);
      try { sessionStorage.setItem(STORAGE_LOADING_KEY, 'true'); } catch (e) { /* ignore */ }

      try {
        const response = await axios.post(
          process.env.REACT_APP_CHAT_API_URL,
          { message: newMessage },
          { headers: { 'Content-Type': 'application/json' }, timeout: 10000 }
        );

        setMessages(prev => {
          const next = [
            ...prev,
            {
              text: response?.data?.message ?? JSON.stringify(response?.data) ?? 'No response body',
              type: 'system-message'
            }
          ];
          
          try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (_) { }
          return next;
        });
      } catch (error) {

        setMessages(prev => {
            let botResponse = BOT_RESPONSES[Math.floor(Math.random() * BOT_RESPONSES.length)];
            const next = [...prev, { text: botResponse, type: 'system-message' }];
            try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (_) { }
            return next;
          });
      } finally {
        setIsLoading(false);
        try { sessionStorage.setItem(STORAGE_LOADING_KEY, 'false'); } catch (_) { }
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        data-focus-mode-hide="true"
        className={`fixed inset-0 z-30 bg-black transition-opacity duration-300 ${
          isVisible ? 'opacity-50 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        data-focus-mode-hide="true"
        className={`fixed left-0 top-0 h-screen z-[60] w-full sm:w-96 bg-white dark:bg-slate-800 shadow-2xl flex flex-col transition-transform duration-300 transform ${
          isVisible ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-blue-600 to-blue-500 dark:from-blue-700 dark:to-blue-600">
          <h2 className="text-lg sm:text-xl font-bold text-white dark:text-white">Ask AI</h2>
          <button
            onClick={onClose}
            aria-label="Close sidebar"
            className="ml-auto p-2 sm:p-3 hover:bg-blue-700 dark:hover:bg-blue-800 active:bg-blue-800 dark:active:bg-blue-900 rounded-lg transition-colors duration-200 flex items-center justify-center"
            title="Close chat (Esc)"
          >
            <FaTimes className="text-white text-xl sm:text-2xl" />
          </button>
        </div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 bg-white dark:bg-slate-800">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-center">
              <p className="text-slate-500 dark:text-slate-400 text-sm sm:text-base">Start a conversation by typing a message below...</p>
            </div>
          ) : (
            <>
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${
                    msg.type === 'user-message' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-xs px-4 py-2 rounded-lg text-sm sm:text-base ${
                      msg.type === 'user-message'
                        ? 'bg-blue-600 dark:bg-blue-500 text-white rounded-br-none'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 rounded-bl-none'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="flex gap-2 px-4 py-3 bg-slate-100 dark:bg-slate-700 rounded-lg rounded-bl-none">
                    <div className="w-2 h-2 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                    <div className="w-2 h-2 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    <div className="w-2 h-2 bg-slate-400 dark:bg-slate-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Area */}
        <div className="p-4 sm:p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700">
          <div className="flex gap-2">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              rows="3"
              aria-label="Message input"
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm sm:text-base text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 bg-white dark:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-400 focus:border-transparent disabled:bg-slate-200 dark:disabled:bg-slate-700 disabled:text-slate-500 dark:disabled:text-slate-400 resize-none transition-all duration-200"
            />
            <button
              onClick={handleSendMessage}
              disabled={!message.trim() || isLoading}
              aria-label="Send message"
              className="px-4 py-2 bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 active:bg-blue-800 dark:active:bg-blue-700 disabled:bg-slate-300 dark:disabled:bg-slate-600 text-white font-semibold rounded-lg transition-colors duration-200 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <FaPaperPlane className="text-lg" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingSidebar;